/**
 * Session timeout utility for handling expired authentication sessions
 * Provides centralized session expiry detection and user notification
 */

class SessionTimeoutHandler {
  constructor() {
    this.isHandlingTimeout = false;
  }

  /**
   * Check if a response indicates session timeout
   * @param {Response} response - Fetch API response object
   * @returns {boolean} - True if session has expired
   */
  isSessionExpired(response) {
    if (!response) return false;
    // Considera expiração quando a API responde 401 (não autorizado)
    // e também 403 (proibido), comum quando token inválido/expirado
    return response.status === 401 || response.status === 403;
  }

  /**
   * Handle session timeout by showing notification and redirecting
   * @param {string} customMessage - Optional custom timeout message
   * @param {boolean} forceLogoutRegularUser - Force logout of regular users (default: false)
   */
  handleSessionTimeout(customMessage = null, forceLogoutRegularUser = false) {
    if (this.isHandlingTimeout) return;
    this.isHandlingTimeout = true;

    try {
      // Limpa dados de sessão de administrador
      try { localStorage.removeItem('adminToken'); } catch {}
      try { localStorage.removeItem('adminUser'); } catch {}

      // Se o usuário atual for admin, também realizar logout completo
      try {
        const currentUserRaw = localStorage.getItem('currentUser');
        if (currentUserRaw) {
          const user = JSON.parse(currentUserRaw);
          const shouldClearCurrent = forceLogoutRegularUser || user?.isAdmin === true;
          if (shouldClearCurrent) {
            localStorage.removeItem('currentUser');
          }
        }
      } catch {}

      // Opcional: armazenar mensagem para eventual exibição pós-redirect
      if (customMessage) {
        try { localStorage.setItem('sessionTimeoutMessage', String(customMessage)); } catch {}
      }

      // Redireciona para a página inicial e força re-login
      this.redirectToHomepage();
    } finally {
      // Evita ficar preso em estado de "handling"
      setTimeout(() => { this.isHandlingTimeout = false; }, 1000);
    }
  }

  /**
   * Display a professional timeout notification
   * @param {string} message - Notification message
   */
  showTimeoutNotification(message) {
    // Implementação simples: usar alert se quisermos feedback imediato.
    // Mantemos discreto para evitar conflitos com UI.
    if (message) {
      try {
        // Evita alert em ambientes de teste/SSR
        if (typeof window !== 'undefined' && window?.alert) {
          // Comentado por padrão para não interromper fluxo
          // window.alert(message);
        }
      } catch {}
    }
  }

  /**
   * Redirect user to homepage
   */
  redirectToHomepage() {
    try {
      // Nosso app usa hash para páginas; garantir que vamos para "entrada"
      if (typeof window !== 'undefined') {
        // Tentar limpar hash e depois definir para entrada
        try { window.location.hash = ''; } catch {}
        try { window.location.hash = 'entrada'; } catch {}
        // Como fallback, garantir navegação para raiz
        try { window.location.href = '/'; } catch {}
      }
    } catch {}
  }

  /**
   * Create a fetch wrapper that automatically handles session timeouts
   * @param {string} url - Request URL
   * @param {object} options - Fetch options
   * @returns {Promise<Response>} - Fetch response
   */
  async fetchWithTimeoutHandling(url, options = {}) {
    const response = await fetch(url, options);
    if (this.isSessionExpired(response)) {
      this.handleSessionTimeout();
    }
    return response;
  }
}

// Create singleton instance
const sessionTimeoutHandler = new SessionTimeoutHandler();

export default sessionTimeoutHandler;

// Export individual methods for convenience
export const {
  isSessionExpired,
  handleSessionTimeout,
  showTimeoutNotification,
  redirectToHomepage,
  fetchWithTimeoutHandling
} = sessionTimeoutHandler;